/**
 * Pendulum/Radiesthesia page - interactive pendulum tool
 * Premium feature for energy diagnosis with fullscreen board
 * Dark mystical theme with glowing neon energy zones
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, Compass, Lock, Maximize2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '@/components/ui';
import { RadiesthesiaCanvas } from '@/components/features/RadiesthesiaCanvas';
import { RADIESTHESIA_ZONES, hexToRgba } from '@/components/features/radiesthesiaZones';
import { useLanguage, useAuthStore } from '@/store';
import { Link, useNavigate } from 'react-router-dom';
import { getBottomNavTranslation, getPendulumTranslation } from '@/utils';
import { ROUTES } from '@/config/routes';
import type { Language } from '@/types';
import { hasAccess } from '@/utils/access';

/** Pendulum lives under Profil → Alati: a back link to Profil on every state of the page. */
function BackToProfile({ language }: { language: Language }) {
  return (
    <Link
      to={ROUTES.profile}
      className="mb-4 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      {getBottomNavTranslation('profile', language)}
    </Link>
  );
}

export function PendulumPage() {
  const language = useLanguage();
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  // Every plan and the trial get the same access (R2, R5).
  const isPremium = hasAccess(user);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenCanvasRef = useRef<HTMLCanvasElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenStarsRef = useRef<Array<{ x: number; y: number; r: number; a: number }> | null>(null);

  const t = useCallback(
    (key: Parameters<typeof getPendulumTranslation>[0]) => getPendulumTranslation(key, language),
    [language]
  );

  // Draw fullscreen canvas with dark mystical theme
  const drawFullscreenCanvas = useCallback(() => {
    const canvas = fullscreenCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to window size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const isLandscape = canvasWidth > canvasHeight;

    // Reserve space for energy zones legend at top
    const legendHeight = 50;

    let centerX: number, centerY: number, radius: number;

    if (isLandscape) {
      // Landscape: semicircle takes most of the screen, legend at top
      radius = Math.min(canvasHeight * 0.75 - 40, canvasWidth / 2 - 60);
      centerX = canvasWidth / 2;
      centerY = canvasHeight - 30;
    } else {
      // Portrait: leave room for legend at top, more padding for numbers
      radius = Math.min(canvasWidth / 2 - 60, canvasHeight * 0.5);
      centerX = canvasWidth / 2;
      centerY = canvasHeight * 0.72;
    }

    // Dark cosmic background gradient
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    const bgGradient = ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, Math.max(canvasWidth, canvasHeight) * 0.8
    );
    bgGradient.addColorStop(0, '#1a1040');
    bgGradient.addColorStop(0.5, '#141028');
    bgGradient.addColorStop(1, '#0d0b1a');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Star field
    if (!fullscreenStarsRef.current) {
      fullscreenStarsRef.current = Array.from({ length: 60 }, () => ({
        x: Math.random(),
        y: Math.random() * 0.75,
        r: Math.random() * 1.2 + 0.3,
        a: Math.random() * 0.35 + 0.08,
      }));
    }
    for (const star of fullscreenStarsRef.current) {
      ctx.beginPath();
      ctx.arc(star.x * canvasWidth, star.y * canvasHeight, star.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${star.a})`;
      ctx.fill();
    }

    // Draw energy zones legend below close button area
    const zoneLegendY = legendHeight + 20;
    const totalLegendWidth = canvasWidth * 0.85;
    const zoneWidth = totalLegendWidth / RADIESTHESIA_ZONES.length;
    const startX = (canvasWidth - totalLegendWidth) / 2;

    ctx.font = '600 13px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    RADIESTHESIA_ZONES.forEach((zone, i) => {
      const x = startX + i * zoneWidth + zoneWidth / 2;

      // Colored pill
      const pillW = Math.min(zoneWidth - 8, 90);
      const pillH = 22;
      const pillX = x - pillW / 2;
      const pillY = zoneLegendY - pillH / 2;

      ctx.save();
      ctx.shadowColor = hexToRgba(zone.coreColor, 0.4);
      ctx.shadowBlur = 8;
      ctx.fillStyle = hexToRgba(zone.coreColor, 0.7);
      ctx.beginPath();
      ctx.roundRect(pillX, pillY, pillW, pillH, 4);
      ctx.fill();
      ctx.restore();

      // Range text inside pill
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.font = '700 11px Inter, system-ui, sans-serif';
      ctx.fillText(`${zone.start}-${zone.end}`, x, zoneLegendY);

      // Label below pill
      ctx.fillStyle = 'rgba(255, 255, 255, 0.60)';
      ctx.font = '400 10px Inter, system-ui, sans-serif';
      ctx.fillText(t(zone.label), x, zoneLegendY + pillH / 2 + 12);
    });

    // Draw zones with neon glow
    RADIESTHESIA_ZONES.forEach((zone) => {
      const startAngle = Math.PI - (zone.start / 100) * Math.PI;
      const endAngle = Math.PI - (zone.end / 100) * Math.PI;

      const gradient = ctx.createRadialGradient(
        centerX, centerY, radius * 0.3,
        centerX, centerY, radius
      );
      gradient.addColorStop(0, hexToRgba(zone.coreColor, 0.45));
      gradient.addColorStop(1, hexToRgba(zone.coreColor, 0.85));

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, endAngle, startAngle);
      ctx.lineTo(centerX, centerY);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Soft zone boundary
      ctx.save();
      ctx.shadowColor = 'rgba(255, 255, 255, 0.08)';
      ctx.shadowBlur = 4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.10)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    });

    // Outer aura glow
    ctx.save();
    ctx.shadowColor = 'rgba(107, 76, 230, 0.40)';
    ctx.shadowBlur = 25;
    ctx.strokeStyle = 'rgba(107, 76, 230, 0.18)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + 2, 0, Math.PI);
    ctx.stroke();
    ctx.restore();

    // Scale markings
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.50)';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = '500 18px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i <= 100; i += 10) {
      const angle = Math.PI - (i / 100) * Math.PI;
      const x1 = centerX + Math.cos(angle) * (radius - 30);
      const y1 = centerY + Math.sin(angle) * (radius - 30);
      const x2 = centerX + Math.cos(angle) * radius;
      const y2 = centerY + Math.sin(angle) * radius;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.50)';
      ctx.stroke();

      const textX = centerX + Math.cos(angle) * (radius + 35);
      const textY = centerY + Math.sin(angle) * (radius + 35);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fillText(i.toString(), textX, textY);
    }

    // Minor marks
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 2;
    for (let i = 5; i < 100; i += 10) {
      const angle = Math.PI - (i / 100) * Math.PI;
      const x1 = centerX + Math.cos(angle) * (radius - 15);
      const y1 = centerY + Math.sin(angle) * (radius - 15);
      const x2 = centerX + Math.cos(angle) * radius;
      const y2 = centerY + Math.sin(angle) * radius;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Labels with text glow
    ctx.save();
    ctx.shadowColor = 'rgba(255, 255, 255, 0.25)';
    ctx.shadowBlur = 8;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.70)';
    ctx.font = '700 24px Poppins, system-ui, sans-serif';
    ctx.textAlign = 'center';

    // Center label (NEUTRAL)
    ctx.font = '700 24px Poppins, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.70)';
    const topY = centerY - radius * 0.65;
    ctx.fillText(t('canvasNeutral'), centerX, topY);

    // Subtitle
    ctx.shadowColor = 'rgba(255, 255, 255, 0.12)';
    ctx.shadowBlur = 4;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '400 16px Inter, system-ui, sans-serif';
    ctx.fillText(t('canvasNeutralSub'), centerX, topY + 24);

    ctx.restore(); // restore from label glow save

    // Center area — dark purple
    const innerRadius = radius * 0.25;
    const centerGradient = ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, innerRadius
    );
    centerGradient.addColorStop(0, '#2a1854');
    centerGradient.addColorStop(0.6, '#1a1040');
    centerGradient.addColorStop(1, '#3a2070');

    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
    ctx.fillStyle = centerGradient;
    ctx.fill();

    // Purple glow stroke
    ctx.save();
    ctx.shadowColor = 'rgba(107, 76, 230, 0.50)';
    ctx.shadowBlur = 20;
    ctx.strokeStyle = 'rgba(107, 76, 230, 0.50)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.restore();

    // NO (left) / YES (right) text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.font = '700 20px Poppins, system-ui, sans-serif';
    ctx.textBaseline = 'middle';

    ctx.textAlign = 'right';
    ctx.fillText(t('canvasNo'), centerX - 12, centerY);
    ctx.textAlign = 'left';
    ctx.fillText(t('canvasYes'), centerX + 12, centerY);

    // Vertical dividing line
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - innerRadius * 0.55);
    ctx.lineTo(centerX, centerY + innerRadius * 0.55);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Glowing center energy point
    ctx.save();
    ctx.shadowColor = '#5da3f7';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
    ctx.fillStyle = '#5da3f7';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.30)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }, [t]);

  // Handle fullscreen
  const enterFullscreen = () => {
    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  // Draw canvas and request fullscreen API once overlay is mounted
  useEffect(() => {
    if (!isFullscreen) return;

    drawFullscreenCanvas();

    const container = fullscreenContainerRef.current;
    if (container?.requestFullscreen) {
      container.requestFullscreen().catch(() => {});
    }
  }, [isFullscreen, drawFullscreenCanvas]);

  // Handle resize in fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    const handleResize = () => {
      drawFullscreenCanvas();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen, drawFullscreenCanvas]);

  // Handle ESC key and fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [isFullscreen]);

  if (!isAuthenticated) {
    return (
      <div className="container py-8 max-w-lg mx-auto">
        <BackToProfile language={language} />
        <Card className="text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <Lock className="h-16 w-16 text-muted-foreground" />
            </div>
            <CardTitle>{t('loginRequired')}</CardTitle>
            <CardDescription>{t('loginDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')}>{t('login')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="container py-8 max-w-lg mx-auto">
        <BackToProfile language={language} />
        <Card className="gradient-border text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Compass className="h-16 w-16 text-primary" />
                <Lock className="h-6 w-6 text-lukaaji-gold absolute -bottom-1 -right-1" />
              </div>
            </div>
            <CardTitle className="text-gradient">{t('premiumFeature')}</CardTitle>
            <CardDescription>{t('premiumDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{t('premiumUse')}</p>
            <Button className="gradient-primary" onClick={() => navigate('/pricing')}>
              {t('upgrade')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="container py-8">
        <BackToProfile language={language} />
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <Button onClick={enterFullscreen} className="gradient-primary">
            <Maximize2 className="h-4 w-4 mr-2" />
            {t('openFullscreen')}
          </Button>
        </div>

        {/* Instructions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">{t('howToUse')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>{t('instruction1')}</li>
              <li>{t('instruction2')}</li>
              <li>{t('instruction3')}</li>
              <li>{t('instruction4')}</li>
              <li>{t('instruction5')}</li>
              <li>{t('instruction6')}</li>
            </ol>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">{t('preview')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <RadiesthesiaCanvas
              width={400}
              height={280}
              isPreview={true}
              language={language}
              className="rounded-lg border border-white/10"
            />
            <p className="text-sm text-muted-foreground mt-4 text-center">
              {t('previewNote')}
            </p>
          </CardContent>
        </Card>

        {/* Energy Zones */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('energyZones')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {RADIESTHESIA_ZONES.map((zone) => (
                <div key={zone.label} className="flex items-center gap-3">
                  <div
                    className="w-16 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      backgroundColor: hexToRgba(zone.coreColor, 0.7),
                      boxShadow: `0 0 8px ${hexToRgba(zone.coreColor, 0.35)}`,
                    }}
                  >
                    {zone.start}-{zone.end}
                  </div>
                  <span className="text-sm">{t(zone.label)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fullscreen Overlay — dark background */}
      {isFullscreen && (
        <div
          ref={fullscreenContainerRef}
          className="fixed inset-0 z-[100] bg-[#0d0b1a]"
        >
          <button
            onClick={exitFullscreen}
            className="absolute top-4 right-4 z-[101] p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <canvas
            ref={fullscreenCanvasRef}
            className="w-full h-full"
            style={{ backgroundColor: '#0d0b1a' }}
          />
        </div>
      )}
    </>
  );
}
